import { Fragment, useEffect, useState, type ReactNode } from 'react';
import type { CheckStatus, HoldState, RegionControl, Reservation, ServiceState, WorkspaceState } from '@vinz/shared';
import { useAuth } from '../auth.js';
import { formatAgo, formatLeft, formatLocal, viewerTimeZone } from '../lib/format.js';
import { useStartSweep, useStateQuery } from '../use-api.js';

const GITHUB_ORG = 'rewindio';
const R5_TITLE = 'Holds arrive with R5 — there is nothing to act on yet.';
const BOT_CREATOR = 'github-actions[bot]';

function commitUrl(repo: string, sha: string): string {
  return `https://github.com/${GITHUB_ORG}/${repo}/commit/${sha}`;
}

/** Every link on this page leaves it for GitHub; none should take the dashboard with it. */
export function ExtLink({ href, className, title, children }: {
  href: string; className?: string; title?: string; children: ReactNode;
}) {
  return (
    <a href={href} className={className} title={title} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

const ROW_CLASS: Record<HoldState, string> = {
  free: '',
  held: 'held',
  restoring: 'restoring',
  restore_failed: 'failed',
};

/**
 * The one workspace a collapsed row speaks for: the one whose SHA most observed workspaces share, so
 * on a half-applied deploy the row shows what the majority is running and the service cell flags the
 * rest. `workspaces[0]` is position 0 of the CONFIGURED list, observed or not, so it is never used
 * blindly; when nothing has reported there is genuinely nothing to show.
 */
export function representativeWorkspace(workspaces: WorkspaceState[]): WorkspaceState | undefined {
  const observed = workspaces.filter((w) => w.sha !== undefined);
  const counts = new Map<string, number>();
  for (const w of observed) counts.set(w.sha as string, (counts.get(w.sha as string) ?? 0) + 1);
  let best: WorkspaceState | undefined;
  for (const w of observed) {
    if (!best || (counts.get(w.sha as string) ?? 0) > (counts.get(best.sha as string) ?? 0)) best = w;
  }
  return best;
}

const CONTROL_LABEL: Record<RegionControl, string> = { on: 'Vinz live', off: 'Vinz off', unset: 'Vinz not set up' };
const CONTROL_TITLE: Record<RegionControl, string> = {
  on: 'VINZ_REGION_CONTROL is on: /reserve is accepted and deploys honour a hold',
  off: 'VINZ_REGION_CONTROL is set but not on: /reserve is refused and deploys ignore holds',
  unset: 'VINZ_REGION_CONTROL is not set on this repo: /reserve is refused and deploys ignore holds',
};

/**
 * Go-live flips repos on one at a time, so which ones Vinz is actually live on is read off this line.
 * Only "on" is emphasised; nothing is shown until a poll has read the variable, rather than a guess.
 */
function RegionControlLine({ control }: { control?: RegionControl }) {
  if (!control) return null;
  return (
    <div className={control === 'on' ? 'sub tone-brand' : 'sub'} title={CONTROL_TITLE[control]}>
      {control === 'on' ? <strong>{CONTROL_LABEL.on}</strong> : CONTROL_LABEL[control]}
    </div>
  );
}

/** Reservable, but the labels sync strips an unlisted branch-deploy label, so branch deploys there are unreliable. */
function LabelMissingBadge({ labelMissing }: { labelMissing?: boolean }) {
  if (!labelMissing) return null;
  return (
    <div className="sub">
      <span className="pill-flag" title="This repo’s .github/labels.yml has no branch-deploy label, so the sync may delete it and branch deploys cannot be relied on. It is still reservable.">
        label missing
      </span>
    </div>
  );
}

const RUNTIME_TITLE: Record<NonNullable<ServiceState['runtime']>, string> = { ecs: 'ECS', lambda: 'Lambda' };

/**
 * What the service runs on, as a glyph before its name: a box for ECS containers, λ for Lambda. The
 * word is on hover and read to screen readers. Nothing for a service with no shared deploy workflow.
 */
function RuntimeIcon({ runtime }: { runtime?: ServiceState['runtime'] }) {
  if (!runtime) return null;
  return (
    <span className={`runtime runtime-${runtime}`} title={RUNTIME_TITLE[runtime]} role="img" aria-label={RUNTIME_TITLE[runtime]}>
      {runtime === 'ecs' ? (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round">
          <path d="M8 1.75 13.75 5v6L8 14.25 2.25 11V5z" />
          <path d="M2.25 5 8 8.25 13.75 5M8 8.25v6" />
        </svg>
      ) : (
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M4.5 2.5h1.75L11.5 13.5" />
          <path d="M8.1 6.9 4.25 13.5" />
        </svg>
      )}
    </span>
  );
}

/**
 * The repo name with a break opportunity after each underscore, so atlas_sns_lambda_forwarder wraps at
 * a word boundary the way hyphenated names already do, instead of splitting mid-word.
 */
function ServiceLabel({ repo }: { repo: string }) {
  const parts = repo.split('_');
  return (
    <span className="service-label">
      {parts.map((part, i) => (
        <Fragment key={i}>{part}{i < parts.length - 1 && <>_<wbr /></>}</Fragment>
      ))}
    </span>
  );
}

function ServiceCell({ svc }: { svc: ServiceState }) {
  // The count is of workspaces that actually reported; an unread one is named under What is deployed
  // instead of being folded into a number that implies the reading covers it.
  const observed = svc.workspaces.filter((w) => w.sha !== undefined).length;
  const total = observed > 0 ? observed : svc.workspaces.length;
  const rep = representativeWorkspace(svc.workspaces);
  const differ = svc.mixed && rep
    ? svc.workspaces.filter((w) => w.sha !== undefined && w.sha !== rep.sha).length
    : 0;
  // With no strict majority (1:1, 1:1:1) there is no "odd one out" to count, and the row's PR is just
  // one of them - say they disagree instead of implying which target is behind.
  const split = differ > 0 && differ >= observed - differ;
  // A single-target service - nearly all of them - needs no sub-line. Only a service that deploys as
  // several ECS services (restore-service: restore, restore-copy, restore-sync) says so, with the
  // names on hover, because that is what makes "N of M differ" mean something.
  if (total <= 1) {
    return (
      <>
        <div className="service-name"><RuntimeIcon runtime={svc.runtime} /><ServiceLabel repo={svc.repo} /></div>
        <RegionControlLine control={svc.regionControl} />
        <LabelMissingBadge labelMissing={svc.labelMissing} />
      </>
    );
  }
  return (
    <>
      <div className="service-name"><RuntimeIcon runtime={svc.runtime} /><ServiceLabel repo={svc.repo} /></div>
      <RegionControlLine control={svc.regionControl} />
      <LabelMissingBadge labelMissing={svc.labelMissing} />
      <div className="sub" title={svc.workspaces.map((w) => w.workspace).join('\n')}>
        {total} deploy targets
        {differ > 0 && (
          <> · <strong className="differ">{split ? 'targets disagree' : `${differ} of ${total} differ`}</strong></>
        )}
      </div>
    </>
  );
}

function WhoHasItCell({ holdState, reservation, releasedAt, check, reservable, reason, nowMs }: {
  holdState: HoldState; reservation?: Reservation; releasedAt?: string; check: CheckStatus;
  reservable: boolean; reason?: string; nowMs: number;
}) {
  // Neither "Free" nor "Unknown" is true of a service Vinz cannot reserve: nobody can take it, so
  // the question the column answers does not apply. Say so, and why, so the fix is findable. A
  // service that lost its wiring mid-hold is still held, though - that hold, its holder and any
  // failed restore keep their normal treatment, with the reason added beneath.
  const notReservable = !reservable && reason ? <div className="sub">Not reservable: {reason}</div> : null;
  if (!reservable && holdState === 'free') {
    return (
      <>
        <span className="pill pill-neutral">Not reservable</span>
        {reason && <div className="sub" style={{ marginTop: 7 }}>{reason}</div>}
      </>
    );
  }
  let pill: ReactNode;
  let detail: string | undefined;
  switch (holdState) {
    case 'held':
      pill = <span className="pill pill-brand">Held</span>;
      detail = reservation?.reason;
      break;
    case 'restoring':
      pill = <span className="pill pill-yellow">Restoring main</span>;
      detail = 'hold ended, environment being reset';
      break;
    case 'restore_failed':
      pill = <span className="pill pill-red">Restore failed</span>;
      detail = reservation ? `hold ended ${formatAgo(releasedAt ?? reservation.expires_at, nowMs)}` : undefined;
      break;
    case 'free':
    default:
      // "Free" tells someone they can deploy, so it needs a reading behind it. When holds could not
      // be read - the poll is failing or stale, or has never succeeded - there may be a hold nobody
      // can see, and saying Free would invite deploying over it.
      if (check.state === 'cannot_check' || !check.lastSuccessAt) {
        return (
          <>
            <span className="pill pill-neutral">Unknown</span>
            <div className="sub" style={{ marginTop: 7 }}>Holds can’t be read right now</div>
          </>
        );
      }
      // An unknown future state reads as free rather than borrowing another state's alarm.
      return (
        <>
          <span className="pill pill-green">Free</span>
          <div className="sub" style={{ marginTop: 7 }}>Nobody — anyone can deploy</div>
        </>
      );
  }
  return (
    <>
      {pill}
      {reservation && <div className="holder">{reservation.holder}</div>}
      {detail && <div className="sub">{detail}</div>}
      {notReservable}
    </>
  );
}

/**
 * One inert control that names nobody, because "is this the viewer's own hold" is a question Vinz
 * cannot answer yet.
 *
 * `reservation.holder` is a GitHub handle (`alex-rewind`); the viewer is a Google identity
 * (`alex.smith@rewind.io`). Nothing maps one to the other, and a `holder === emailPrefix` comparison
 * is false for everyone whose handle is not their email prefix — it would offer the holder a button
 * reading "Nudge <their own name>". Extend and Release need the answer to be "yes", Nudge needs "no",
 * so with no answer none of them can be offered honestly.
 *
 * TODO(R5): R5 is what grants holds, so R5 is where the mapping lands — a viewer→handle lookup, or
 * a holder email carried on the reservation payload. Restore the Extend/Release/Nudge treatment and
 * the viewer identity this component needs to choose between them at the same time.
 */
function HeldActions() {
  return (
    <div className="acts">
      <button type="button" disabled title={R5_TITLE} className="act act-secondary">Hold actions arrive with R5</button>
    </div>
  );
}

function ForHowLongCell({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  const { holdState, reservation } = svc;
  switch (holdState) {
    case 'held': {
      if (!reservation) return <span className="muted">—</span>;
      const grantedMs = Date.parse(reservation.granted_at);
      const expiresMs = Date.parse(reservation.expires_at);
      const totalMs = expiresMs - grantedMs;
      const pct = Math.min(100, Math.max(0, totalMs > 0 ? ((nowMs - grantedMs) / totalMs) * 100 : 100));
      const extended = reservation.extensions > 0 ? `extended ${reservation.extensions}×` : 'not extended';
      return (
        <>
          <div className="time-left tone-brand">{formatLeft(reservation.expires_at, nowMs)}</div>
          <div className="sub">until {formatLocal(reservation.expires_at)} · {extended}</div>
          <div className="bar" role="progressbar" aria-valuenow={Math.round(pct)} aria-valuemin={0} aria-valuemax={100}>
            <i style={{ width: `${pct}%` }} />
          </div>
          <HeldActions />
        </>
      );
    }
    case 'restoring':
      return (
        <>
          {reservation && (
            <div className="time-left tone-yellow">
              {svc.releasedAt ? `Released ${formatAgo(svc.releasedAt, nowMs)}` : `Expired ${formatAgo(reservation.expires_at, nowMs)}`}
            </div>
          )}
          <div className="sub">main redeploying automatically</div>
        </>
      );
    case 'restore_failed':
      return (
        <>
          <div className="time-left tone-red">Restore failed</div>
          <div className="sub tone-red">redeploy of main did not complete — still on their branch</div>
          <div className="acts">
            <button type="button" disabled title={R5_TITLE} className="act act-secondary">Retry restore</button>
          </div>
        </>
      );
    case 'free':
    default:
      return <span className="muted">—</span>;
  }
}

function DeployedCell({ svc }: { svc: ServiceState }) {
  const rep = representativeWorkspace(svc.workspaces);
  if (!rep) return <span className="muted">—</span>;
  // representativeWorkspace only ever returns a workspace with a SHA.
  const { ref, pr } = rep;
  const sha = rep.sha as string;
  const unobserved = svc.workspaces.filter((w) => w.sha === undefined).map((w) => w.workspace);
  // The PR says what is being tested; the branch is only the fallback for a deploy with no PR (main,
  // or a branch nobody opened one for). The SHA stays reachable on hover rather than taking a line.
  const detail = [ref, sha.slice(0, 8)].filter(Boolean).join(' @ ');
  return (
    <>
      {pr ? (
        <div className="pr-line">
          <ExtLink href={pr.url} className="ref" title={detail}>
            <strong>#{pr.number}</strong> {pr.title}
          </ExtLink>
          {pr.merged && <span className="pill-merged">merged</span>}
        </div>
      ) : (
        ref && (
          <ExtLink href={commitUrl(svc.repo, sha)} className="branch" title={detail}>
            {ref}
          </ExtLink>
        )
      )}
      {/* A workspace that has never deployed, not two workspaces disagreeing: the steady state for a
          service that does not use all its configured workspaces, so it is quiet - but still said,
          because the row otherwise implies its reading covers workspaces it does not. */}
      {unobserved.length > 0 && <div className="sub">no reading yet: {unobserved.join(', ')}</div>}
    </>
  );
}

function DeployedByCell({ svc, nowMs }: { svc: ServiceState; nowMs: number }) {
  const w = representativeWorkspace(svc.workspaces);
  if (!w) return <span className="muted">—</span>;
  const { attribution } = w;
  const name =
    attribution.kind === 'person' ? <div>{attribution.name}</div>
    : attribution.kind === 'ci' ? <div><span className="muted" title={`last main deploy by ${attribution.name}`}>CI</span></div>
    : <div><span className="muted">Vinz · {attribution.name === 'branch deploy' ? 'branch deploy' : 'auto-restore'}</span></div>;
  return (
    <>
      {name}
      {w.actorIsBot && <div className="sub">via {BOT_CREATOR}</div>}
      {w.deployedAt && (
        <div className="sub">
          {w.runUrl ? <ExtLink href={w.runUrl}>{formatAgo(w.deployedAt, nowMs)}</ExtLink> : formatAgo(w.deployedAt, nowMs)}
        </div>
      )}
    </>
  );
}

function CheckedCell({ check, nowMs }: { check: CheckStatus; nowMs: number }) {
  if (check.state === 'current') {
    return (
      <>
        <span className="pill pill-green">Current</span>
        {check.lastSuccessAt && <div className="sub">{formatAgo(check.lastSuccessAt, nowMs)}</div>}
      </>
    );
  }
  if (check.state === 'cannot_check') {
    const lastRead = check.lastSuccessAt ? `last read ${formatAgo(check.lastSuccessAt, nowMs)}` : 'no successful read yet';
    return (
      <>
        <span className="pill pill-yellow">Can’t check</span>
        <div className="sub">{check.reason ? `${lastRead} · ${check.reason}` : lastRead}</div>
      </>
    );
  }
  return (
    <>
      <span className="pill pill-neutral">Never checked</span>
      <div className="sub">no successful read yet</div>
    </>
  );
}

export interface StatusTableProps {
  services: ServiceState[];
  nowMs: number;
}

export function StatusTable({ services, nowMs }: StatusTableProps) {
  return (
    <table className="grid">
      <thead>
        <tr>
          <th scope="col" style={{ width: '14%' }}>Service</th>
          <th scope="col" style={{ width: '19%' }}>Who has it</th>
          <th scope="col" style={{ width: '17%' }}>For how long</th>
          <th scope="col" style={{ width: '25%' }}>What is deployed</th>
          <th scope="col" style={{ width: '13%' }}>Deployed by</th>
          <th scope="col" style={{ width: '12%' }}>Checked</th>
        </tr>
      </thead>
      <tbody>
        {services.map((svc) => (
          <tr key={svc.repo} className={ROW_CLASS[svc.holdState] ?? ''}>
            <td><ServiceCell svc={svc} /></td>
            <td><WhoHasItCell holdState={svc.holdState} reservation={svc.reservation} releasedAt={svc.releasedAt} check={svc.check}
              reservable={svc.reservable} reason={svc.reason} nowMs={nowMs} /></td>
            <td><ForHowLongCell svc={svc} nowMs={nowMs} /></td>
            <td><DeployedCell svc={svc} /></td>
            <td><DeployedByCell svc={svc} nowMs={nowMs} /></td>
            <td><CheckedCell check={svc.check} nowMs={nowMs} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** The newest successful sweep across services - what "last sweep" means, not any row's observedAt. */
/**
 * Whether a forced sweep requested at `since` has reached every row. The poller writes each service's
 * reading as it gets to it, so the newest timestamp (lastSweep) moves as soon as the FIRST service is
 * done - treating that as "landed" stopped the fast polling while later rows still showed the old age.
 * Only services currently readable are waited for: one the poller cannot check never gets a newer
 * lastSuccessAt, and the SWEEP_WAIT_MS cap still bounds the wait.
 */
export function sweepLanded(services: ServiceState[], since: string): boolean {
  const readable = services.filter((s) => s.check.state === 'current');
  return readable.length > 0 && readable.every((s) => (s.check.lastSuccessAt ?? '') >= since);
}

function lastSweep(services: ServiceState[]): string | undefined {
  return services
    .map((s) => s.check.lastSuccessAt)
    .filter((t): t is string => t !== undefined)
    .sort()
    .at(-1);
}

function CannotCheckBanner({ services }: { services: ServiceState[] }) {
  const stuck = services.filter((s) => s.check.state === 'cannot_check').map((s) => s.repo);
  if (stuck.length === 0) return null;
  const names = stuck.join(', ');
  return (
    <div className="banner-warning" role="status">
      <span aria-hidden="true" className="banner-mark">!</span>
      <div>
        <strong>Vinz can’t reach GitHub for {names}.</strong> This is the last reading it managed.{' '}
        <em>{stuck.length === 1 ? `${names} itself is fine` : 'The services themselves are fine'}</em> — it is this page that has
        stopped being refreshed.
      </div>
    </div>
  );
}

/** Give up waiting for a forced sweep to land after this; the scheduled refresh takes over again. */
export const SWEEP_WAIT_MS = 90 * 1000;

/**
 * Starts a sweep now rather than waiting for the next five-minute one. The API rate-limits this to one
 * a minute across everybody, so a refusal is an ordinary answer, shown as when the last one started.
 */
function SweepControl({ idToken, sweeping, onStarted, nowMs }: {
  idToken: string | null; sweeping: boolean; onStarted: (requestedAt: string) => void; nowMs: number;
}) {
  const [note, setNote] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const startSweep = useStartSweep();

  async function sweepNow() {
    if (!idToken) return;
    setSending(true);
    setNote(null);
    try {
      const body = await startSweep(idToken);
      if (body.status === 'started') {
        onStarted(body.requestedAt);
      } else {
        const last = body.lastRequestedAt ? `swept ${formatAgo(body.lastRequestedAt, nowMs)} · ` : '';
        setNote(`${last}try again in ${body.retryAfterSeconds}s`);
      }
    } catch (err) {
      // Kept visible in devtools: the note below cannot say whether it was the network, auth or a 502.
      console.error('sweep failed', err);
      setNote((err as Error)?.name === 'UnauthorizedError' ? 'session expired — signing you back in, try again' : 'couldn’t start a sweep');
    } finally {
      setSending(false);
    }
  }

  return (
    <>
      <button
        type="button"
        className={`sweep-btn${sweeping ? ' spinning' : ''}`}
        onClick={() => void sweepNow()}
        disabled={sweeping || sending}
        aria-label={sweeping ? 'Sweeping…' : 'Sweep now'}
        title={sweeping ? 'Sweeping…' : 'Sweep now — check GitHub for new deploys'}
      >
        <svg viewBox="0 0 16 16" width="13" height="13" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9" />
          <path d="M13.5 2.5v3h-3" />
        </svg>
      </button>
      {note && <div className="sweep-note" role="status">{note}</div>}
    </>
  );
}

export function Status() {
  const { idToken } = useAuth();
  const [sweepingSince, setSweepingSince] = useState<string | null>(null);
  const { data, isPending, isError, error } = useStateQuery(idToken, { sweeping: sweepingSince !== null });
  const landed = data ? lastSweep(data.services) : undefined;
  const sweepDone = data && sweepingSince ? sweepLanded(data.services, sweepingSince) : false;

  // A forced sweep is over when a newer successful sweep shows up, or after SWEEP_WAIT_MS regardless.
  useEffect(() => {
    if (sweepDone) setSweepingSince(null);
  }, [sweepDone]);
  useEffect(() => {
    if (!sweepingSince) return;
    const t = setTimeout(() => setSweepingSince(null), SWEEP_WAIT_MS);
    return () => clearTimeout(t);
  }, [sweepingSince]);

  if (isPending) return <div className="card">Loading services…</div>;
  if (isError) {
    return <div className="card">Couldn’t load service state: {error instanceof Error ? error.message : String(error)}</div>;
  }

  const nowMs = Date.now();
  const sweep = landed;
  return (
    <div>
      <div className="page-head">
        <div>
          <h2>Staging us-west-2</h2>
          <p>One row per service, in alphabetical order. A service that deploys to several targets collapses to one row while they agree.</p>
        </div>
        <div className="page-head-meta">
          <div className="sweep-line">
            <span>Checked every 5 min{sweep && <> · last sweep {formatAgo(sweep, nowMs)}</>}</span>
            <SweepControl idToken={idToken} sweeping={sweepingSince !== null} onStarted={setSweepingSince} nowMs={nowMs} />
          </div>
          <div>Times in {viewerTimeZone()}</div>
        </div>
      </div>
      <CannotCheckBanner services={data.services} />
      <div className="table-card">
        <StatusTable services={data.services} nowMs={nowMs} />
      </div>
      <p className="page-note">
        <strong>Expiry restores the environment.</strong> When a hold ends, Vinz redeploys <span className="mono">main</span> to
        that workspace automatically, so a service is never left free-to-take but still running someone’s branch. A free
        service on <span className="mono">main</span> reads <strong>CI</strong> rather than a person — a name there would
        imply somebody has it. A free service on a <em>branch</em> still names the person, because that is the anomaly.
      </p>
    </div>
  );
}
